// src/admin/pages/warehouse/WarehousesPage.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Fab,
  Drawer,
  Paper,
  TextField,
  InputAdornment,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { Warehouse } from '../../../enums/product';
import { WarehouseService } from './warehouseService';
import WarehouseTable from './WarehouseTable';
import WarehouseView from './WarehouseView';
import WarehouseDrawer from './WarehouseDrawer';
import ToastNotification from '../common/Toastnotification';
import { useAuth } from '../../../contexts/AuthContext';
import { useWarehousesPaginated, useWarehouseStats, WAREHOUSE_KEYS } from '../../../hooks/useWarehouses';
import { useQueryClient } from '@tanstack/react-query';

const WAREHOUSES_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// ── LoadingOverlay ────────────────────────────────────────────────────────────
interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();

  const overlayBg =
    theme.palette.mode === 'dark'
      ? 'rgba(26, 29, 36, 0.85)'
      : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        pointerEvents: 'all',
        borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading warehouses...
        </Typography>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WarehousesPage
// ─────────────────────────────────────────────────────────────────────────────
const WarehousesPage: React.FC = () => {
const { currentUser, hasPermission, isSuperAdmin } = useAuth();

const canCreate   = isSuperAdmin || hasPermission('warehouse.create');
const canEdit     = isSuperAdmin || hasPermission('warehouse.edit');
const canDelete   = isSuperAdmin || hasPermission('warehouse.delete');
const canTransfer = isSuperAdmin || hasPermission('warehouse.transfer');  const queryClient = useQueryClient();

  // Invalidates ALL warehouse-related queries (list + stats) so both the
  // table and the stats cards refresh after any create / update / delete.
  const invalidateAll = () =>
    queryClient.invalidateQueries({ queryKey: WAREHOUSE_KEYS.all });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit' | 'view'>('add');
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });
  const [deleteTarget, setDeleteTarget] = useState<Warehouse | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── React Query hooks ─────────────────────────────────────────────────────
  // Each unique combination of { page, search, status, pageSize } is its own
  // cache entry. React Query serves cached results instantly on revisit and
  // only re-fetches after invalidateAll() is called.
  const {
    data: warehousesData,
    isLoading: warehousesLoading,
    isFetching: warehousesIsFetching,
    isError: warehousesError,
    error: warehousesErrorObj,
  } = useWarehousesPaginated({
    page: currentPage,
    search: searchQuery,
    status: statusFilter,
    pageSize: WAREHOUSES_PER_PAGE,
  });

  const { data: statsData } = useWarehouseStats();

  // ── Derived values ────────────────────────────────────────────────────────
  const warehouses = warehousesData?.warehouses ?? [];
  const totalWarehouses = warehousesData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalWarehouses / WAREHOUSES_PER_PAGE);
  const loading = warehousesLoading || warehousesIsFetching;

  const stats = statsData ?? {
    total: 0,
    active: 0,
    inactive: 0,
    totalCapacity: 0,
    usedCapacity: 0,
    availableCapacity: 0,
    utilizationRate: 0,
  };

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info'
  ) => setToast({ open: true, message, severity });

  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  const resetToFirstPage = () => setCurrentPage(1);

  // ── Pagination handler ────────────────────────────────────────────────────
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) =>
    setCurrentPage(page);

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Filters ───────────────────────────────────────────────────────────────
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    resetToFirstPage();
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(
    Boolean
  ).length;

  // ── Add / Edit / View ─────────────────────────────────────────────────────
  const handleAddWarehouse = () => {
    setSelectedWarehouse(null);
    setDrawerMode('add');
    setIsDrawerOpen(true);
  };

  const handleEditWarehouse = (warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  };

  const handleViewWarehouse = (warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse);
    setIsViewOpen(true);
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteWarehouse = (warehouse: Warehouse) => setDeleteTarget(warehouse);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      await WarehouseService.deleteWarehouse(deleteTarget.id!);
      setDeleteTarget(null);
      // Bust the cache so the table and stats both reflect the deletion.
      invalidateAll();
      resetToFirstPage();
      showToast('Warehouse deleted successfully', 'success');
    } catch (err: any) {
      showToast(
        'Failed to delete warehouse: ' + (err.message || 'Unknown error'),
        'error'
      );
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  // ── Drawer close ──────────────────────────────────────────────────────────
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedWarehouse(null);
  };

  const handleViewClose = () => {
    setIsViewOpen(false);
    setSelectedWarehouse(null);
  };

  // ── Post-save: invalidate cache so list + stats refresh ───────────────────
  // WarehouseDrawer calls WarehouseService.createWarehouse / updateWarehouse
  // directly (not via a React Query mutation). Calling invalidateAll() here
  // marks every warehouse cache entry as stale, which triggers an automatic
  // background refetch — the same effect as a React Query mutation's
  // onSuccess invalidation in BrandManagement.
  const handleWarehouseSaved = () => {
    handleDrawerClose();
    invalidateAll();
    resetToFirstPage();
    showToast(
      drawerMode === 'add'
        ? 'Warehouse created successfully'
        : 'Warehouse updated successfully',
      'success'
    );
  };

  // ── Manual refresh (also called from WarehouseView after stock transfers) ─
  const handleRefresh = () => {
    invalidateAll();
    resetToFirstPage();
    showToast('Warehouses refreshed', 'info');
  };

  // ── Initial full-page loading state ───────────────────────────────────────
  if (loading && warehouses.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        flexDirection="column"
      >
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading warehouses...
        </Typography>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <WarehouseIcon
              sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}
              >
                Warehouses
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your warehouse locations and capacity
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignSelf: { xs: 'flex-start', sm: 'auto' },
            }}
          >
            <Button
              onClick={handleRefresh}
              variant="outlined"
              startIcon={<RefreshIcon />}
              disabled={loading}
            >
              Refresh
            </Button>
            {canCreate && (
            <Button
              onClick={handleAddWarehouse}
              variant="contained"
              startIcon={<AddIcon />}
            >
              Add Warehouse
            </Button>
            )}
          </Stack>
        </Stack>

        {/* ── Error banner ── */}
        {warehousesError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleRefresh}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            }
          >
            {(warehousesErrorObj as Error)?.message || 'Failed to fetch warehouses'}
          </Alert>
        )}

        {/* ── Stats Cards ── */}
        <Grid
          container
          spacing={{ xs: 1, sm: 1.5, md: 2 }}
          sx={{ mb: 2 }}
          alignItems="stretch"
        >
          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Total Warehouses
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {stats.total}
                </Typography>
                <Box
                  sx={{
                    mt: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" color="success.main" noWrap>
                      {stats.active} Active
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'text.disabled',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {stats.inactive} Inactive
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Total Capacity
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {stats.totalCapacity.toLocaleString()}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    units
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Used Capacity
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {stats.usedCapacity.toLocaleString()}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {(
                      (stats.usedCapacity / stats.totalCapacity) * 100 || 0
                    ).toFixed(1)}
                    % utilized
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Available Capacity
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {stats.availableCapacity.toLocaleString()}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    units available
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search by name, code, city... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button
                  onClick={clearFilters}
                  variant="text"
                  size="small"
                  startIcon={<ClearIcon />}
                >
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active Only</MenuItem>
                      <MenuItem value="inactive">Inactive Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${warehouses.length} warehouses matching your filters`
                        : `Showing ${warehouses.length} warehouses on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {/* ── Empty state ── */}
        {warehouses.length === 0 && !loading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
            }}
          >
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <StorageIcon
                  sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }}
                />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters
                    ? 'No Warehouses Match Your Filters'
                    : 'No Warehouses Found'}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
                >
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No warehouses available yet. Create your first warehouse to get started.'}
                </Typography>
                {hasActiveFilters ? (
                  <Button
                    onClick={clearFilters}
                    variant="outlined"
                    startIcon={<ClearIcon />}
                  >
                    Clear Filters
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddWarehouse}
                  >
                    Add Warehouse
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* ── Table + Pagination ── */}
        {warehouses.length > 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 'auto', md: 0 },
            }}
          >
            <Box
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: 'auto',
                mb: 2,
                minHeight: { xs: 'auto', md: 0 },
                position: 'relative',
              }}
            >
              <WarehouseTable
                warehouses={warehouses}
                onView={handleViewWarehouse}
                onEdit={handleEditWarehouse}
                onDelete={handleDeleteWarehouse}
                deleteLoading={deleteLoading}
                isDark={isDark}
                canEdit={canEdit}
  canDelete={canDelete}
              />
            </Box>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Showing{' '}
                    {totalWarehouses === 0
                      ? 0
                      : (currentPage - 1) * WAREHOUSES_PER_PAGE + 1}
                    –{Math.min(currentPage * WAREHOUSES_PER_PAGE, totalWarehouses)} of{' '}
                    {totalWarehouses} warehouses
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── View Drawer ── */}
      <Drawer
        anchor="right"
        open={isViewOpen}
        onClose={handleViewClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: '100%', md: 1000 }, maxWidth: 1000 },
        }}
      >
        {selectedWarehouse && (
          <WarehouseView
            warehouseId={selectedWarehouse.id!}
            onClose={handleViewClose}
            onEdit={handleEditWarehouse}
            canTransfer={canTransfer}  
          />
        )}
      </Drawer>

      {/* ── Add / Edit Drawer ── */}
      <WarehouseDrawer
        isOpen={isDrawerOpen}
        mode={drawerMode}
        warehouse={selectedWarehouse}
        onClose={handleDrawerClose}
        onSave={handleWarehouseSaved}
        onShowToast={showToast}
      />

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={!!deleteTarget}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Warehouse</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>"{deleteTarget?.name}"</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={
              deleteDialogLoading ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteDialogLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Toast ── */}
      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />

      {/* ── Mobile FAB ── */}
      {canCreate && (
      <Fab
        color="primary"
        aria-label="add warehouse"
        sx={{
          position: 'fixed',
          // Sits above the globally-mounted AdminAIChat FAB (also bottom:24,
          // right:24) so the two don't overlap/hide each other on mobile.
          bottom: 96,
          right: 24,
          display: { xs: 'flex', md: 'none' },
        }}
        onClick={handleAddWarehouse}
      >
        <AddIcon />
      </Fab>
      )}
    </Box>
  );
};

export default WarehousesPage;